import * as DG from "ui/dialogs"
import { useState } from "react"

import { AgencyClientProduct, StartWeekDay } from "models/agency"
import { ApiError, ErrorsMap } from "api/http/utils"
import { queryCache, useMutation } from "react-query"

import AutoCompleteInput from "components/inputs/AutoComplete"
import Button from "ui/buttons/Button"
import FormControl from "@material-ui/core/FormControl"
import { TextInput } from "components/inputs/TextInput"
import { agencyClientStyles } from "../../AgencyClients/Details/styles"
import { patchProduct } from "api/products"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import useEditProductsReducer, {
  EditProductStateActionType,
} from "./EditProducts.reducer"
import { CircularProgressOverlay } from "ui/utils/CircularProgressOverlay"

export type EditProductProps = {
  onClose: () => void
  product: AgencyClientProduct
  options: StartWeekDay[]
}

function EditProduct(props: EditProductProps) {
  const { onClose, product, options } = props
  const { t } = useI18n()
  const { id } = useParams<{ id: string }>()
  const [errors, setErrors] = useState<ErrorsMap | undefined>()
  const [state, dispatch] = useEditProductsReducer(product)

  const [patch, { status }] = useMutation(patchProduct, {
    onSuccess: async () => {
      queryCache.removeQueries("client-agency-products")
      onClose()
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const handleSubmit = () => {
    const { id, clientId, name, code, division, startWeekDay } = state
    patch({ id, clientId, name, code, division, startWeekDay })
  }

  return (
    <DG.Dialog
      open={true}
      onClose={onClose}
      id={id}
      disableEscapeKeyDown
      keepMounted
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id}> {t("products.edit.title")}</DG.DialogTitle>
        <DG.DialogClose onClose={onClose} />
      </DG.DialogHeader>

      <DG.DialogContent paddingTop>
        <CircularProgressOverlay status={status}>
          <div>
            <FormControl variant="outlined">
              <div>
                <TextInput
                  name="name"
                  label={t("products.input.name")}
                  value={state?.name || ""}
                  margin="normal"
                  size="medium"
                  className={agencyClientStyles.fieldWidth}
                  onChange={(payload) =>
                    dispatch({
                      type: EditProductStateActionType.GENERIC_CHANGE,
                      payload,
                    })
                  }
                  error={!!errors && !!errors["name"]}
                  errorMessage={!!errors ? errors["name"] : ""}
                />
              </div>
              <div>
                <TextInput
                  name="code"
                  value={state?.code || ""}
                  label={t("products.input.code")}
                  margin="normal"
                  size="medium"
                  className={agencyClientStyles.codeTextfield}
                  onChange={(payload) =>
                    dispatch({
                      type: EditProductStateActionType.GENERIC_CHANGE,
                      payload,
                    })
                  }
                  error={!!errors && !!errors["code"]}
                  errorMessage={!!errors ? errors["code"] : ""}
                />
              </div>
              <div>
                <TextInput
                  name="division"
                  value={state?.division || ""}
                  label={t("products.input.division")}
                  margin="normal"
                  size="medium"
                  className={agencyClientStyles.fieldWidth}
                  onChange={(payload) =>
                    dispatch({
                      type: EditProductStateActionType.GENERIC_CHANGE,
                      payload,
                    })
                  }
                  error={!!errors && !!errors["division"]}
                  errorMessage={!!errors ? errors["division"] : ""}
                />
              </div>
              <div>
                <AutoCompleteInput<StartWeekDay>
                  id="startWeekDay"
                  name="startWeekDay"
                  label={t("products.input.start-week-day")}
                  size="medium"
                  margin="normal"
                  disableClearable
                  fetchOnFilter={false}
                  options={options}
                  value={state?.startWeekDay || null}
                  onChange={(val) =>
                    dispatch({
                      type: EditProductStateActionType.START_WEEK_DAY,
                      payload: val as StartWeekDay,
                    })
                  }
                  getOptionSelected={(option, value) => option === value}
                  getOptionLabel={(option) =>
                    !!option ? t(`products.input.start-week-day.${option}`) : ""
                  }
                  renderOption={(option) => (
                    <span>{t(`products.input.start-week-day.${option}`)}</span>
                  )}
                />
              </div>
            </FormControl>
          </div>
        </CircularProgressOverlay>
      </DG.DialogContent>
      <DG.DialogFooter>
        <DG.DialogActions>
          <Button variant="outlined" color="primary" onClick={onClose}>
            {t("products.button.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="new_client"
            onClick={handleSubmit}
          >
            {t("products.button.edit")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

export default EditProduct
